import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export interface HelpTopic {
  id: string;
  title: string;
  file: string;
}

const FALLBACK_TOPICS: HelpTopic[] = [
  { id: 'getting-started', title: 'Getting Started', file: 'assets/help/getting-started.html' },
  { id: 'reset-password', title: 'Reset Password', file: 'assets/help/reset-password.html' },
  { id: 'create-post', title: 'Create a Post', file: 'assets/help/create-post.html' },
  { id: 'post-social-card', title: 'Post a Social Card', file: 'assets/help/post-social-card.html' },
  { id: 'connect-facebook', title: 'Connect Facebook', file: 'assets/help/connect-facebook.html' },
  { id: 'connect-x', title: 'Connect X (Twitter)', file: 'assets/help/connect-x.html' },
  { id: 'connect-wordpress', title: 'Connect WordPress', file: 'assets/help/connect-wordpress.html' },
  { id: 'configure-ai', title: 'Configure AI', file: 'assets/help/configure-ai.html' },
  { id: 'publish-targets', title: 'Publish Targets', file: 'assets/help/publish-targets.html' },
  { id: 'user-settings', title: 'User Settings', file: 'assets/help/user-settings.html' },
  { id: 'troubleshooting', title: 'Troubleshooting', file: 'assets/help/troubleshooting.html' },
  { id: 'glossary', title: 'Glossary', file: 'assets/help/glossary.html' }
];

@Injectable({ providedIn: 'root' })
export class HelpService {
  private topics: HelpTopic[] = [];
  private categories: Array<{ category: string; documents: Array<{ id: string; title: string }> }> = [];
  // Promise that resolves when initial TOC/categories have been loaded
  private _initResolve: (() => void) | null = null;
  public ready: Promise<void> = new Promise<void>((resolve) => { this._initResolve = resolve; });
  
  constructor(private http: HttpClient) {
    this.init();
  }

  /**
   * Try to load the docs TOC from the API. If that fails, fall back to embedded
   * topics so the UI can still show an index and local pages.
   */
  init(): void {
    this.http.get<any>(`${environment.apiUrl}/help/docs`).pipe(
      catchError(() => of({ docs: [] }))
    ).subscribe((res) => {
      // Prefer the grouped `categories` shape when available
      if (res && Array.isArray(res.categories) && res.categories.length > 0) {
        this.categories = res.categories.map((c: any) => ({ category: c.category, documents: Array.isArray(c.documents) ? c.documents.map((d: any) => ({ id: d.id, title: d.title })) : [] }));
        // Flatten into topics for compatibility with existing UI consumers
        const flat: HelpTopic[] = [];
        for (const c of this.categories) {
          for (const d of c.documents) {
            flat.push({ id: d.id, title: d.title, file: `assets/help/${d.id}.html` });
          }
        }
        this.topics = flat;
      } else {
        const docs = res && res.docs ? res.docs : [];
        if (docs && docs.length > 0) {
          this.topics = docs.map((d: any) => ({ id: d.id, title: d.title, file: `assets/help/${d.id}.html` }));
          // Build a simple categories view (uncategorized)
          this.categories = [{ category: 'uncategorized', documents: docs.map((d: any) => ({ id: d.id, title: d.title })) }];
        } else {
          // API unavailable or returned empty list -> use fallback topics
          this.topics = FALLBACK_TOPICS.slice();
          this.categories = [{ category: 'local', documents: this.topics.map(t => ({ id: t.id, title: t.title })) }];
        }
      }
      // signal ready to any waiters
      if (this._initResolve) {
        this._initResolve();
        this._initResolve = null;
      }
    }, () => {
      this.topics = FALLBACK_TOPICS.slice();
      this.categories = [{ category: 'local', documents: this.topics.map(t => ({ id: t.id, title: t.title })) }];
      if (this._initResolve) {
        this._initResolve();
        this._initResolve = null;
      }
    });
  }

  listTopics(): HelpTopic[] {
    return this.topics.slice();
  }

  /** Return the grouped categories TOC (preferred shape). */
  listCategories(): Array<{ category: string; documents: Array<{ id: string; title: string }> }> {
    return this.categories.slice();
  }

  getTopicById(id: string): HelpTopic | undefined {
    return this.topics.find(t => t.id === id);
  }

  /**
   * Fetch a topic HTML. First attempt the API route (`/api/help/file/...`). If that
   * fails (for example when running the UI locally without the API), fall back to
   * fetching the local asset directly (e.g. `assets/help/...`).
   */
  fetchTopicHtml(filePath: string): Observable<string> {
    if (filePath && filePath.startsWith('assets/help/')) {
      const rel = filePath.substring('assets/help/'.length).replace(/\.html$/i, '');
      const apiUrl = `${environment.apiUrl}/help/file/${rel}?format=json`;
      // Try API JSON first, then local asset.
      return this.http.get<any>(apiUrl).pipe(
        map((r) => (r && r.html) ? r.html : ''),
        catchError(() => this.http.get(filePath, { responseType: 'text' }))
      );
    }
    // If it's not a local help file path, attempt direct fetch.
    return this.http.get(filePath, { responseType: 'text' }).pipe(
      catchError(() => of('<p>Unable to load help content.</p>'))
    );
  }

  fetchTopicHtmlById(id: string): Observable<string> {
    const apiUrl = `${environment.apiUrl}/help/file/${encodeURIComponent(id)}?format=json`;
    return this.http.get<any>(apiUrl).pipe(
      map((r) => (r && r.html) ? r.html : ''),
      catchError(() => this.http.get(`assets/help/${id}.html`, { responseType: 'text' }))
    );
  }

  fetchIndexHtml(): Observable<string> {
    // Prefer the server-provided TOC (grouped categories) and render it into HTML.
    return this.http.get<any>(`${environment.apiUrl}/help/docs`).pipe(
      map((res) => {
        if (res && Array.isArray(res.categories) && res.categories.length > 0) {
          const parts: string[] = [];
          for (const c of res.categories) {
            parts.push(`<h3>${c.category}</h3>`);
            parts.push('<ul>');
            for (const d of c.documents || []) {
              parts.push(`<li><a href="#" data-id="${d.id}">${d.title}</a></li>`);
            }
            parts.push('</ul>');
          }
          return parts.join('\n');
        }
        // Fallback: flat docs list
        const docs = res && res.docs ? res.docs : [];
        if (docs && docs.length > 0) {
          return '<ul>' + docs.map((t: any) => `<li><a href="#" data-id="${t.id}">${t.title}</a></li>`).join('') + '</ul>';
        }
        // Final fallback: local topics
        const html = '<ul>' + this.listTopics().map(t => `<li><a href="#" data-id="${t.id}">${t.title}</a></li>`).join('') + '</ul>';
        return html;
      }),
      catchError(() => {
        const html = '<ul>' + this.listTopics().map(t => `<li><a href="#" data-id="${t.id}">${t.title}</a></li>`).join('') + '</ul>';
        return of(html);
      })
    );
  }

  searchHelp(query: string): Observable<any> {
    return this.http.get(`${environment.apiUrl}/help/search?q=${encodeURIComponent(query)}`);
  }
}
